import React, { useMemo, useCallback, useState } from 'react';
import { Typography, Card, CardContent, Switch, Tooltip, Box, Chip } from '@mui/material';
import { Percent } from '@mui/icons-material';
import { ResponsiveLine } from '@nivo/line';
import { useTheme } from '@mui/material/styles';
import { useChartData } from './useChartData';
import { CHART_VIEWS } from './ItemAnalytics';

function UsageChartInventory({ timeSeriesData, period, itemData, dateRange, selectedPeriod, setSelectedPeriod }) {
    const theme = useTheme();
    const [normalizeByCase, setNormalizeByCase] = useState(false);

    // Use the shared chart data hook
    const rawChartData = useChartData(timeSeriesData, CHART_VIEWS.INVENTORY_VIEW, period, dateRange);

    // Process chart data to optionally normalize by case count
    const chartData = useMemo(() => {
        if (!rawChartData || !normalizeByCase) return rawChartData;

        return rawChartData.map(series => ({
            ...series,
            data: series.data.map(point => {
                const cases = point.cases || 0;
                return {
                    ...point,
                    y: cases > 0 ? point.y / cases : 0,
                    originalY: point.y // Keep original value for tooltip
                };
            })
        }));
    }, [rawChartData, normalizeByCase]);

    // Calculate dynamic min/max for line chart based on data and standard deviation
    const lineChartYScale = useMemo(() => {
        if (!chartData || chartData.length === 0) {
            return { type: 'linear', min: 0, max: 'auto', stacked: false, reverse: false };
        }

        // Extract all y values from both series
        const allYValues = [];
        chartData.forEach(series => {
            series.data.forEach(point => {
                if (typeof point.y === 'number' && !isNaN(point.y)) {
                    allYValues.push(point.y);
                }
            });
        });

        if (allYValues.length === 0) {
            return { type: 'linear', min: 0, max: 'auto', stacked: false, reverse: false };
        }

        // Calculate min and max data points
        const minValue = Math.min(...allYValues);

        // Calculate standard deviations for each series
        const calculateSD = (values) => {
            if (values.length <= 1) return 0;
            const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
            const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (values.length - 1);
            return Math.sqrt(variance);
        };

        const usedValues = chartData.find(s => s.id === 'total_used')?.data.map(d => d.y).filter(y => typeof y === 'number' && !isNaN(y)) || [];
        const pickedValues = chartData.find(s => s.id === 'total_picked')?.data.map(d => d.y).filter(y => typeof y === 'number' && !isNaN(y)) || [];

        const usedSD = calculateSD(usedValues);
        const pickedSD = calculateSD(pickedValues);
        const maxSD = Math.max(usedSD, pickedSD);

        // Calculate adjusted min/max
        const adjustedMin = Math.max(0, minValue - 0.5 * maxSD); // Don't go below 0

        return {
            type: 'linear',
            min: adjustedMin,
            max: 'auto',
            stacked: false,
            reverse: false
        };
    }, [chartData]);

    // Memoized format period label for x-axis
    const formatPeriodLabel = useCallback((periodStr) => {
        if (period === 'day') {
            // Parse as local date to avoid timezone issues
            const [year, month, day] = periodStr.split('-').map(Number);
            const date = new Date(year, month - 1, day); // month is 0-indexed
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else if (period === 'week') {
            return `Week ${periodStr.split('-W')[1] || periodStr}`;
        } else if (period === 'month') {
            const [year, month] = periodStr.split('-').map(Number);
            const date = new Date(year, month - 1);
            return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        } else if (period === 'quarter') {
            const [year, quarter] = periodStr.split('-Q');
            return `Q${quarter} ${year}`;
        }
        return periodStr;
    }, [period]);

    // Handle point selection
    const handlePointClick = useCallback((point) => {
        const periodId = point.data.x;
        setSelectedPeriod(selectedPeriod === periodId ? null : periodId);
    }, [selectedPeriod, setSelectedPeriod]);

    // Memoized tooltip component for better performance
    const CustomTooltip = useMemo(() => {
        const tooltipStyles = {
            container: {
                padding: 12,
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 4,
                boxShadow: theme.shadows[4],
                minWidth: 200,
                maxWidth: 300
            },
            title: {
                marginBottom: 8
            },
            valueRow: {
                display: 'flex',
                alignItems: 'center',
                marginBottom: 4
            },
            colorBox: {
                width: 12,
                height: 12,
                marginRight: 8,
                borderRadius: 2
            },
            valueText: {
                textTransform: 'capitalize'
            },
            caseText: {
                color: theme.palette.text.secondary,
                fontSize: '0.875rem'
            }
        };

        const TooltipComponent = ({ point }) => {
            const currentValue = normalizeByCase ? (point.data.originalY || 0) : (point.data.y || 0);
            const normalizedValue = point.data.y || 0;
            
            const formattedValue = `${currentValue}`;
            
            const formattedNormalizedValue = `${normalizedValue}`;
            
            const seriesName = point.id.split('.')[0];

            return (
                <div style={tooltipStyles.container}>
                    <div style={tooltipStyles.title}>
                        <strong>{formatPeriodLabel(point.data.x)}</strong>
                    </div>
                    <div style={tooltipStyles.valueRow}>
                        <div
                            style={{
                                ...tooltipStyles.colorBox,
                                background: point.serieColor
                            }}
                        />
                        <span style={tooltipStyles.valueText}>
                            {seriesName}: {normalizeByCase ? formattedNormalizedValue : formattedValue}
                            {normalizeByCase ? ' per case' : ''}
                        </span>
                    </div>
                    {normalizeByCase && (
                        <div style={tooltipStyles.caseText}>
                            Total: {formattedValue}
                        </div>
                    )}
                    <div style={tooltipStyles.caseText}>
                        Cases: {point.data.cases || 0}
                    </div>
                </div>
            );
        };

        TooltipComponent.displayName = 'CustomTooltip';
        return TooltipComponent;
    }, [theme, formatPeriodLabel]);

    if (!chartData.length) {
        return (
            <Card>
                <CardContent>
                    <Typography color="text.secondary" align="center">
                        No usage data available for the selected time period
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        {'Inventory trends in quantity over time'}
                        {normalizeByCase ? ' (per case)' : ''}
                    </Typography>
                    <Tooltip title="Normalize per case">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Percent sx={{ fontSize: '1rem', opacity: normalizeByCase ? 1 : 0.5, mr: 0.5 }} />
                            <Switch 
                                checked={normalizeByCase}
                                onChange={(e) => setNormalizeByCase(e.target.checked)}
                                size="small"
                            />
                        </Box>
                    </Tooltip>
                </Box>
                <div style={{ height: 400 }}>
                    <ResponsiveLine
                        key={`line-${timeSeriesData?.length || 0}-${period}-${dateRange?.startDate}-${dateRange?.endDate}`}
                        data={chartData}
                        curve={'monotoneX'}
                        margin={{ top: 25, right: 130, bottom: 110, left: 80 }}
                        xScale={{ type: 'point' }}
                        yScale={lineChartYScale}
                        yFormat=" >-.2f"
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 12,
                            tickRotation: -45,
                            legend: 'Time Period',
                            legendOffset: 80,
                            legendPosition: 'middle',
                            format: formatPeriodLabel
                        }}
                        axisLeft={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            legend: normalizeByCase ? 'Quantity per Case' : 'Quantity',
                            legendOffset: -60,
                            legendPosition: 'middle'
                        }}
                        pointLabelYOffset={-12}
                        onClick={handlePointClick}
                        enableArea={true}
                        areaBaselineValue={lineChartYScale.min}
                        areaOpacity={0.1}
                        useMesh={true}
                        colors={[theme.palette.analytics.cardTypes.picked, theme.palette.analytics.cardTypes.used]}
                        legends={[
                            {
                                anchor: 'bottom-right',
                                direction: 'column',
                                justify: false,
                                translateX: 120,
                                translateY: 0,
                                itemsSpacing: 0,
                                itemDirection: 'left-to-right',
                                itemWidth: 100,
                                itemHeight: 20,
                                itemOpacity: 0.85,
                                symbolSize: 12,
                                symbolShape: 'circle',
                                symbolBorderColor: 'rgba(0, 0, 0, .5)',
                                effects: [
                                    {
                                        on: 'hover',
                                        style: {
                                            itemBackground: 'rgba(0, 0, 0, .03)',
                                            itemOpacity: 1
                                        }
                                    }
                                ]
                            }
                        ]}
                        tooltip={CustomTooltip}
                        theme={{
                            background: 'transparent',
                            text: {
                                fill: theme.palette.text.primary
                            },
                            axis: {
                                ticks: {
                                    line: {
                                        stroke: theme.palette.divider
                                    },
                                    text: {
                                        fill: theme.palette.text.primary
                                    }
                                },
                                legend: {
                                    text: {
                                        fill: theme.palette.text.primary
                                    }
                                }
                            },
                            grid: {
                                line: {
                                    stroke: theme.palette.divider,
                                    strokeWidth: 1
                                }
                            }
                        }}
                    />
                </div>
                {selectedPeriod && (
                    <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                            Selected Period: {formatPeriodLabel(selectedPeriod)}
                        </Typography>
                        <Chip 
                            label="Clear Selection" 
                            size="small" 
                            variant="outlined"
                            onClick={() => setSelectedPeriod(null)}
                            sx={{ ml: 2 }}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

export default UsageChartInventory;